/**
 * ApprovalCard — Wave 2D
 *
 * Rendered inside `ToolCallCard` when `policyAction === 'require_approval'`.
 * Shows structured args display + Approve / Deny buttons wired to
 * `POST /agent-approvals/:id/approve` and `POST /agent-approvals/:id/reject`.
 *
 * Endpoint shapes (READ-ONLY — from agent-approvals.ts):
 *   POST /agent-approvals/:id/approve  body: { rationale?: string | null }
 *   POST /agent-approvals/:id/reject   body: { rationale?: string | null }
 *
 * Buttons are disabled while the mutation is pending. On success the card
 * replaces itself with a concise status badge. On error it shows the error
 * message inline — never a toast (the parent chat surface manages toasts).
 *
 * NOTE: `approvalId` is an optional prop because at stream time the backend
 * may not yet have created the `agent_tool_approvals` row — when absent the
 * buttons are disabled with an explanatory label. The parent should pass the
 * id once the row is known (wired via the `approval_id` field on ToolCall,
 * which the SSE `tool_call` event populates after row creation).
 */

import { useState } from 'react';
import { Check, X, Loader2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api-client';

type Decision = 'approved' | 'denied' | null;

interface ApprovalCardProps {
  /**
   * UUID of the `agent_tool_approvals` row. When `undefined` the
   * approval row hasn't been created yet; buttons are disabled.
   */
  approvalId?: string;
  /** Structured tool arguments to display for context. */
  args: Record<string, unknown>;
  /** Optional human-readable summary from the server. */
  summary?: string;
  /** Called after a terminal decision (approved or denied) so the parent
   *  can update the tool call status. */
  onDecision?: (decision: 'approved' | 'denied') => void;
}

export function ApprovalCard({
  approvalId,
  args,
  summary,
  onDecision,
}: ApprovalCardProps) {
  const [pending, setPending] = useState(false);
  const [decision, setDecision] = useState<Decision>(null);
  const [error, setError] = useState<string | null>(null);

  // Terminal state — show status chip instead of buttons.
  if (decision !== null) {
    return (
      <div
        className={cn(
          'mt-3 rounded-lg border px-4 py-3 flex items-center gap-2 text-sm font-medium',
          decision === 'approved'
            ? 'border-green-500/30 bg-green-500/5 text-green-700 dark:text-green-400'
            : 'border-red-500/30 bg-red-500/5 text-red-700 dark:text-red-400',
        )}
      >
        {decision === 'approved' ? (
          <Check className="h-4 w-4 shrink-0" />
        ) : (
          <X className="h-4 w-4 shrink-0" />
        )}
        {decision === 'approved' ? 'Approved — action will proceed' : 'Denied — action was blocked'}
      </div>
    );
  }

  const noApprovalId = !approvalId;

  const handleDecision = async (outcome: 'approve' | 'reject') => {
    if (!approvalId) return;
    setPending(true);
    setError(null);

    try {
      await apiClient.post(
        `/agent-approvals/${approvalId}/${outcome}`,
        { rationale: null },
      );
      const terminal: Decision = outcome === 'approve' ? 'approved' : 'denied';
      setDecision(terminal);
      onDecision?.(terminal);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Request failed';
      setError(msg);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="mt-3 rounded-lg border border-yellow-500/30 bg-yellow-500/5 px-4 py-3 space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Clock className="h-3.5 w-3.5 text-yellow-600 dark:text-yellow-400 shrink-0" />
        <span className="text-xs font-semibold text-yellow-700 dark:text-yellow-400 uppercase tracking-wide">
          Approval required
        </span>
      </div>

      {/* Summary */}
      {summary && (
        <p className="text-xs text-foreground leading-[1.6]">{summary}</p>
      )}

      {/* Args preview */}
      {Object.keys(args).length > 0 && (
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
            Arguments
          </p>
          <pre className="text-[11px] font-mono bg-muted/60 rounded p-2 overflow-x-auto max-h-28 text-foreground/80">
            {JSON.stringify(args, null, 2)}
          </pre>
        </div>
      )}

      {/* Error */}
      {error && (
        <p className="text-xs text-destructive">{error}</p>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-1">
        <Button
          size="sm"
          variant="default"
          className="h-7 text-xs gap-1.5 bg-green-600 hover:bg-green-700 text-white"
          disabled={pending || noApprovalId}
          onClick={() => handleDecision('approve')}
          aria-label="Approve tool action"
        >
          {pending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Check className="h-3 w-3" />
          )}
          Approve
        </Button>

        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs gap-1.5 border-red-500/40 text-red-600 dark:text-red-400 hover:bg-red-500/10"
          disabled={pending || noApprovalId}
          onClick={() => handleDecision('reject')}
          aria-label="Deny tool action"
        >
          {pending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <X className="h-3 w-3" />
          )}
          Deny
        </Button>

        {noApprovalId && (
          <span className="text-[10px] text-muted-foreground italic">
            Waiting for approval ID…
          </span>
        )}
      </div>
    </div>
  );
}
